'use client';

import type { HTMLAttributes, PropsWithChildren } from 'react';
import React, { memo, useContext, useMemo } from 'react';
import styled from 'styled-components';
import type { Brands, Themes } from '@frameio/vapor-theme';
import { themeClassNames } from '@frameio/vapor-theme';
import { useThemeTransition } from './hooks';
import { ThemeProviderContext } from './ThemeProviderContext';
import type {
  AccentNeutralColorsOptions,
  CustomAccentNeutralColors,
  CustomBrand,
  HexOrRgbColorString,
  ThemeContext,
} from './types';
import {
  generateAccentNeutralColors,
  generateCustomAccentNeutralBrand,
  generateCustomBrand,
  getAccentNeutralThemeName,
  getCustomStyles,
} from './utils';

/**
 * Access the provided theme name, which is useful for
 * propagating the theme class name to the children within
 * a Portal.
 * @returns string
 */

export function useThemeName() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.themeName;
}

/**
 * Access the provided theme brand name, which is useful for
 * propagating the brand class name to the children within
 * a Portal.
 * @returns string
 */

export function useBrandName() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.brandName;
}

/**
 * Access the provided theme custom brand, which is useful for
 * propagating the brand colors to the children within
 * a Portal.
 * @returns string
 */

export function useCustomBrand() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.customBrand;
}

/**
 * Access the provided theme custom brand name, which is useful for
 * propagating the theme colors to the children within
 * a Portal.
 * @returns string
 */

export function useAccentNeutralColors() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.accentNeutralColors;
}

/**
 * Access the provided theme accent neutral brand name, which is useful for
 * propagating the brand colors to the children within
 * a Portal.
 * @returns string
 */

export function useAccentNeutralBrand() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.accentNeutralBrand;
}

/**
 * Access the outermost `ThemeProvider`'s values.
 * Used for Portaled elements like Toasts that should
 * always use the outer app's theme.
 * @returns CoreThemeProviderValues
 */
export function useOutermostThemeProviderValues() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.outermostThemeProviderValues;
}

type GetThemeValueOptions = {
  themeContext: ThemeContext;
  prop?: Themes | Brands | CustomBrand | CustomAccentNeutralColors;
  contextKey: 'themeName' | 'brandName' | 'customBrand' | 'accentNeutralColors';
};

type GetThemeValueReturnTypes<T> = T extends 'themeName'
  ? Themes
  : T extends 'brandName'
  ? Brands
  : T extends 'customBrand'
  ? CustomBrand
  : T extends 'accentNeutralColors'
  ? CustomAccentNeutralColors
  : never;

const getThemeValue = <T extends GetThemeValueOptions>({
  themeContext,
  prop,
  contextKey,
}: T): GetThemeValueReturnTypes<T['contextKey']> => {
  return prop ?? themeContext?.[contextKey];
};

/**
 * Create a ThemeProvider to allow consumers to specify
 * which themes to use within react subtrees.
 */

type AccentNeutralBrandName = 'accent-neutral';

export type CustomAccentNeutralTheme = {
  name: AccentNeutralBrandName;
} & AccentNeutralColorsOptions;

export type ThemeOptions = Themes | CustomAccentNeutralTheme;

export type BrandOptions =
  | Brands
  | HexOrRgbColorString
  | AccentNeutralBrandName;

export type CustomAccentNeutralThemeAndBrand = {
  theme: CustomAccentNeutralTheme;
  brand: AccentNeutralBrandName | HexOrRgbColorString;
};

export type ThemeAndBrand = {
  theme?: Themes | null;
  brand?: BrandOptions;
};

// common props for ThemeProvider meant to be composed with CustomAccentNeutralThemeAndBrand or ThemeAndBrand
type ThemeProviderBaseProps = HTMLAttributes<'div'> & PropsWithChildren;

// allows for custom themes and brands to be passed in
export type ThemeProviderProps =
  | (CustomAccentNeutralThemeAndBrand & ThemeProviderBaseProps)
  | (ThemeAndBrand & ThemeProviderBaseProps);

const isCustomAccentNeutralThemeType = (
  themeProp
): themeProp is CustomAccentNeutralTheme => {
  if (!themeProp) {
    return false;
  }
  return typeof themeProp === 'object' && 'backgroundColor' in themeProp;
};

const isCustomBrandType = (
  brandProp: ThemeProviderProps['brand']
): brandProp is HexOrRgbColorString => {
  if (!brandProp) {
    return false;
  }
  return brandProp.includes('#') || brandProp.includes('rgb');
};

const getAccentNeutralBrandPrimaryColor = ({
  isCustomAccentNeutralTheme,
  themeProp,
  themeName,
}) => {
  if (isCustomAccentNeutralTheme) {
    return themeProp.backgroundColor;
  }
  return themeName === 'dark' ? '#FFFFFF' : '#000000';
};

/**
 * @description styled container for the theme provider
 */
const StyledThemeContainer = styled.div<
  Omit<ThemeProviderBaseProps, 'providerId'>
>`
  display: contents;
`;

/**
 * @description merge desired classnames from variadic arguments
 */
export const getClassnames = (...values: string[]) =>
  values.filter(Boolean).join(` `);

function ThemeProviderBase({
  theme: themeProp,
  brand: brandProp,
  children,
  ...props
}: ThemeProviderProps) {
  // By default, inherit the theme from the parent theme provider.
  // This is useful for portal based components that need to inherit the theme
  // via context, since they can't rely on the cascading nature of css.
  const themeContext = useContext(ThemeProviderContext);

  const isCustomAccentNeutralTheme = isCustomAccentNeutralThemeType(themeProp);
  const isCustomBrand = isCustomBrandType(brandProp);
  const isCustomAccentNeutralBrand = brandProp === 'accent-neutral';
  const isRegularBrand = !isCustomBrand && !isCustomAccentNeutralBrand;

  const themeName = isCustomAccentNeutralTheme
    ? getAccentNeutralThemeName({
        backgroundColor: themeProp.backgroundColor,
      })
    : getThemeValue({
        themeContext,
        prop: themeProp,
        contextKey: 'themeName',
      });

  const customBrandFromProp = useMemo(() => {
    if (!isCustomBrand) {
      return undefined;
    }
    return generateCustomBrand({
      primaryColor: brandProp,
    });
  }, [brandProp, isCustomBrand]);

  const accentNeutralBrand = useMemo(() => {
    if (!isCustomAccentNeutralBrand) {
      return undefined;
    }
    return generateCustomAccentNeutralBrand({
      primaryColor: getAccentNeutralBrandPrimaryColor({
        isCustomAccentNeutralTheme,
        themeProp,
        themeName,
      }),
    });
  }, [
    isCustomAccentNeutralBrand,
    isCustomAccentNeutralTheme,
    themeProp,
    themeName,
  ]);

  const customBrand = isRegularBrand
    ? undefined
    : getThemeValue({
        themeContext,
        prop: customBrandFromProp,
        contextKey: 'customBrand',
      });

  const brandName =
    getThemeValue({
      themeContext,
      prop: isCustomAccentNeutralBrand || isCustomBrand ? undefined : brandProp,
      contextKey: 'brandName',
      // fallback to `fio` in the case that we're using a custom theme and the parent doesn't have a brand set so there's no brand in the context
    }) ?? 'fio';

  const accentNeutralColorsFromProp = useMemo(() => {
    if (!isCustomAccentNeutralTheme) {
      return undefined;
    }
    return generateAccentNeutralColors({
      backgroundColor: themeProp.backgroundColor,
      textColor: themeProp.textColor,
    });
  }, [isCustomAccentNeutralTheme, themeProp]);

  const accentNeutralColors = getThemeValue({
    themeContext,
    prop: accentNeutralColorsFromProp,
    contextKey: 'accentNeutralColors',
  });

  const isOutermostThemeProvider =
    themeContext?.outermostThemeProviderValues === undefined;
  const context = useMemo(() => {
    return {
      ...themeContext,
      themeName,
      brandName,
      customBrand,
      accentNeutralBrand,
      accentNeutralColors,
      outermostThemeProviderValues: isOutermostThemeProvider
        ? {
            themeName,
            brandName,
            customBrand,
            accentNeutralBrand,
            accentNeutralColors,
          }
        : themeContext?.outermostThemeProviderValues,
    };
  }, [
    brandName,
    themeName,
    themeContext,
    customBrand,
    accentNeutralBrand,
    accentNeutralColors,
    isOutermostThemeProvider,
  ]);

  const themeTransitionGlobalStyles = useThemeTransition({ context });

  return (
    <ThemeProviderContext.Provider value={context}>
      {themeTransitionGlobalStyles}
      <StyledThemeContainer
        {...props}
        className={getClassnames(
          themeClassNames[themeName],
          themeClassNames[brandName]
        )}
        data-theme={themeName}
        data-theme-brand={brandName}
        data-theme-custom-background={accentNeutralColors?.backgroundColor}
        data-theme-custom-brand={customBrand?.primary}
        data-theme-custom-text={accentNeutralColors?.textColor}
        style={{
          ...props.style,
          ...getCustomStyles({
            customBrand,
            accentNeutralBrand,
            accentNeutralColors,
          }),
        }}
      >
        {children}
      </StyledThemeContainer>
    </ThemeProviderContext.Provider>
  );
}

export const ThemeProvider = memo(ThemeProviderBase);
ThemeProvider.displayName = `VaporThemeProvider`;
